import type * as React from 'react'

import {
  type IntegrationNameEnum,
  IntegrationNames,
} from '../entities/enums/IntegrationNameEnum'
import {
  IntegrationStatuses,
  type IntegrationStatusEnum,
} from '../entities/enums/IntegrationStatusEnum'
import { type Integration } from '../entities/Integration'

type StatusDetailsLoader = () => Promise<{ default: React.FC<{}> }>

const defaultStatusDetailsRegistry: Partial<
  Record<IntegrationStatusEnum, StatusDetailsLoader>
> = {
  [IntegrationStatuses.INITIALIZING]: () =>
    import(
      '../features/configure-merge-connection/components/GenericInitializingAlert'
    ).then(({ GenericInitializingAlert }) => ({
      default: GenericInitializingAlert,
    })),
  [IntegrationStatuses.DISABLED]: () =>
    import(
      '../features/configure-merge-connection/components/DisconnectedAlert'
    ).then(({ DisconnectedAlert }) => ({
      default: DisconnectedAlert,
    })),
  [IntegrationStatuses.DISCONNECTED]: () =>
    import(
      '../features/configure-merge-connection/components/DisconnectedAlert'
    ).then(({ DisconnectedAlert }) => ({
      default: DisconnectedAlert,
    })),
}

export interface PlatformSectionConfig {
  id: string
  presenterComponent: () => Promise<{
    default: React.FC<{
      integration: Integration
    }>
  }>
}

export interface PlatformIntegrationDetailsConfig {
  platform: IntegrationNameEnum
  label: string
  accountInformation: {
    presenterComponent: () => Promise<{
      default: React.FC<{
        integration: Integration
      }>
    }>
  }
  sections: PlatformSectionConfig[]
  statusDetails?: Partial<Record<IntegrationStatusEnum, StatusDetailsLoader>>
}

export const hasIntegrationDetails = (
  name: IntegrationNameEnum
): name is SupportedIntegrationNameEnum => {
  return name in integrationDetailsRegistry
}

export const getStatusDetailsComponent = (
  integrationName: IntegrationNameEnum,
  status: IntegrationStatusEnum
): StatusDetailsLoader | undefined => {
  const platformConfig = hasIntegrationDetails(integrationName)
    ? integrationDetailsRegistry[integrationName]
    : undefined

  const platformSpecificComponent = platformConfig?.statusDetails?.[status]
  return platformSpecificComponent ?? defaultStatusDetailsRegistry[status]
}

export type SupportedIntegrationNameEnum =
  typeof IntegrationNames.MERGE_NETSUITE

export const integrationDetailsRegistry: Readonly<
  Record<SupportedIntegrationNameEnum, PlatformIntegrationDetailsConfig>
> = {
  [IntegrationNames.MERGE_NETSUITE]: {
    platform: IntegrationNames.MERGE_NETSUITE,
    label: 'NetSuite',
    accountInformation: {
      presenterComponent: () =>
        import('../feature-sets/NetSuiteAccountInformation').then(
          ({ NetSuiteAccountInformationPresenter }) => ({
            default: NetSuiteAccountInformationPresenter,
          })
        ),
    },
    sections: [
      {
        id: 'bills',
        presenterComponent: () =>
          import('../feature-sets/NetSuiteBillSettingSection').then(
            ({ NetSuiteBillSettingSectionPresenter }) => ({
              default: NetSuiteBillSettingSectionPresenter,
            })
          ),
      },
      {
        id: 'payments',
        presenterComponent: () =>
          import('../feature-sets/NetSuitePaymentSettingSection').then(
            ({ NetSuitePaymentSettingSectionPresenter }) => ({
              default: NetSuitePaymentSettingSectionPresenter,
            })
          ),
      },
      {
        id: 'chartOfAccounts',
        presenterComponent: () =>
          import(
            '../feature-sets/NetSuiteAccountCodeMasterDataSyncSection'
          ).then(({ NetSuiteAccountCodeMasterDataSyncSectionPresenter }) => ({
            default: NetSuiteAccountCodeMasterDataSyncSectionPresenter,
          })),
      },
      {
        id: 'vendors',
        presenterComponent: () =>
          import('../feature-sets/NetSuiteVendorMasterDataSyncSection').then(
            ({ NetSuiteVendorMasterDataSyncSectionPresenter }) => ({
              default: NetSuiteVendorMasterDataSyncSectionPresenter,
            })
          ),
      },
    ],
    statusDetails: {
      [IntegrationStatuses.INITIALIZING]: () =>
        import(
          '../features/configure-merge-connection/components/NetsuiteInitializingAlert'
        ).then(({ NetsuiteInitializingAlert }) => ({
          default: NetsuiteInitializingAlert,
        })),
      [IntegrationStatuses.DISABLED]: () =>
        import(
          '../features/configure-merge-connection/components/NetsuiteDisconnectedAlert'
        ).then(({ NetsuiteDisconnectedAlert }) => ({
          default: NetsuiteDisconnectedAlert,
        })),
      [IntegrationStatuses.DISCONNECTED]: () =>
        import(
          '../features/configure-merge-connection/components/NetsuiteDisconnectedAlert'
        ).then(({ NetsuiteDisconnectedAlert }) => ({
          default: NetsuiteDisconnectedAlert,
        })),
    },
  },
}
